import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useCallback, useEffect } from 'react';
import { useFloating, autoUpdate, offset, flip, shift, useHover, useFocus, useDismiss, useRole, useInteractions, FloatingPortal, FloatingArrow, arrow, useClick, safePolygon, autoPlacement, } from '@floating-ui/react';
import { Button } from '../../atoms/Button/Button';
import { colors } from '../../../base/styled/variables.styled';
import ChevronLeftIcon from '../../../assets/icons/ChevronLeftIcon';
import ChevronRightIcon from '../../../assets/icons/ChevronRightIcon';
import { TooltipContainer, TooltipHeadline, TooltipSupportingText, TooltipButtonsContainer, TooltipTrigger } from './Tooltip.style';
import { getMeasuringElementStyles } from './Tooltip.utils';
const ARROW_HEIGHT = 6;
const GAP = 2;
export const Tooltip = ({ headline, supportingText, leadingButton, trailingButton, children, placement = 'top', trigger = 'hover', disabled = false, open: controlledOpen, onOpenChange, previousButtonText = "Anterior", nextButtonText = "Próximo", showPaginationIcons = true, buttonLayout = 'grouped', inverse = false, }) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [textPages, setTextPages] = useState([]);
    const [needsPagination, setNeedsPagination] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const arrowRef = React.useRef(null);
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : internalOpen;
    const setOpen = useCallback((value) => {
        if (!isControlled)
            setInternalOpen(value);
        if (onOpenChange)
            onOpenChange(value);
    }, [isControlled, onOpenChange]);
    const { x, y, refs, strategy, context } = useFloating({
        placement: placement === 'auto' ? undefined : placement,
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(GAP + ARROW_HEIGHT),
            placement === 'auto' ? autoPlacement() : flip(),
            shift(),
            // eslint-disable-next-line react-hooks/refs
            arrow({
                element: arrowRef,
            }),
        ],
    });
    const click = useClick(context, {
        enabled: trigger === 'click',
    });
    const hover = useHover(context, {
        enabled: trigger === 'hover',
        handleClose: safePolygon(),
    });
    const focus = useFocus(context, {
        enabled: trigger === 'focus',
    });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'tooltip' });
    const { getReferenceProps, getFloatingProps } = useInteractions([
        click,
        hover,
        focus,
        dismiss,
        role,
    ]);
    useEffect(() => {
        if (open) {
            requestAnimationFrame(() => setIsVisible(true));
        }
        else {
            setIsVisible(false);
        }
    }, [open]);
    useEffect(() => {
        if (!supportingText) {
            setTextPages([]);
            setNeedsPagination(false);
            return;
        }
        const measurePagination = () => {
            const tempDiv = document.createElement('div');
            tempDiv.style.cssText = getMeasuringElementStyles();
            document.body.appendChild(tempDiv);
            const words = supportingText.split(' ');
            const pages = [];
            let currentText = '';
            // Altura disponível para o texto (total do tooltip - padding - headline - botões)
            // 200px (aprox) - 24px (padding) - 24px (headline) - 48px (buttons)
            const MAX_HEIGHT = 100;
            for (const word of words) {
                const testText = currentText ? `${currentText} ${word}` : word;
                tempDiv.textContent = testText;
                if (tempDiv.clientHeight > MAX_HEIGHT && currentText) {
                    pages.push(currentText);
                    currentText = word;
                    tempDiv.textContent = word;
                }
                else {
                    currentText = testText;
                }
            }
            if (currentText) {
                pages.push(currentText);
            }
            document.body.removeChild(tempDiv);
            if (pages.length > 1) {
                setTextPages(pages);
                setNeedsPagination(true);
            }
            else {
                setTextPages([supportingText]);
                setNeedsPagination(false);
            }
        };
        measurePagination();
    }, [supportingText]);
    const handleNextPage = () => {
        if (currentPage < textPages.length - 1) {
            setCurrentPage(prev => prev + 1);
        }
    };
    const handlePrevPage = () => {
        if (currentPage > 0) {
            setCurrentPage(prev => prev - 1);
        }
    };
    // Reset page when closed
    useEffect(() => {
        if (!open) {
            setCurrentPage(0);
        }
    }, [open]);
    if (disabled)
        return _jsx(_Fragment, { children: children });
    return (_jsxs(_Fragment, { children: [_jsx(TooltipTrigger, Object.assign({ ref: refs.setReference }, getReferenceProps(), { children: children })), _jsx(FloatingPortal, { children: open && (_jsxs(TooltipContainer
                // eslint-disable-next-line react-hooks/refs
                , Object.assign({ 
                    // eslint-disable-next-line react-hooks/refs
                    ref: refs.setFloating, style: {
                        position: strategy,
                        top: y !== null && y !== void 0 ? y : 0,
                        left: x !== null && x !== void 0 ? x : 0,
                    }, "$isVisible": isVisible, "$inverse": inverse }, getFloatingProps(), { children: [_jsx(TooltipHeadline, { "$inverse": inverse, children: headline }), supportingText && (_jsx(TooltipSupportingText, { "$inverse": inverse, children: needsPagination ? textPages[currentPage] : supportingText })), (leadingButton || trailingButton || needsPagination) && (_jsx(TooltipButtonsContainer, { "$layout": buttonLayout, "$inverse": inverse, children: needsPagination ? (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "tertiary", size: "small", onClick: handlePrevPage, disabled: currentPage === 0, label: showPaginationIcons ? undefined : previousButtonText, leftSvgIcon: showPaginationIcons ? _jsx(ChevronLeftIcon, {}) : undefined, iconOnly: showPaginationIcons }), _jsx(Button, { variant: "tertiary", size: "small", onClick: handleNextPage, disabled: currentPage === textPages.length - 1, label: showPaginationIcons ? undefined : nextButtonText, rightSvgIcon: showPaginationIcons ? _jsx(ChevronRightIcon, {}) : undefined, iconOnly: showPaginationIcons })] })) : (_jsxs(_Fragment, { children: [leadingButton && (_jsx(Button, { variant: leadingButton.variant || 'tertiary', size: "small", onClick: leadingButton.onClick, disabled: leadingButton.disabled, label: leadingButton.label, leftSvgIcon: leadingButton.leftIcon, rightSvgIcon: leadingButton.rightIcon })), trailingButton && (_jsx(Button, { variant: trailingButton.variant || 'primary', size: "small", onClick: trailingButton.onClick, disabled: trailingButton.disabled, label: trailingButton.label, leftSvgIcon: trailingButton.leftIcon, rightSvgIcon: trailingButton.rightIcon }))] })) })), _jsx(FloatingArrow, { ref: arrowRef, context: context, fill: inverse ? colors.neutral000 : colors.neutral800, height: ARROW_HEIGHT, width: 12 })] }))) })] }));
};
//# sourceMappingURL=Tooltip.js.map